import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorAlert } from '@/components/ErrorAlert';
import { PageHeader } from '@/components/PageHeader';
import { errorText } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { client, orpc } from '@/lib/orpc';
import { meQuery, useMe } from '@/lib/session';

export const Route = createFileRoute('/_app/admin')({
  // UX only: the API enforces the admin role on every procedure itself.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery());
    if (me.role !== 'admin') throw notFound();
  },
  component: AdminPage
});

function AdminPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <PageHeader title={t('admin.title')} />
      <Users />
      <Invites />
      <Health />
    </div>
  );
}

function Users() {
  const { t } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const users = useQuery(orpc.users.list.queryOptions());
  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.users.key() });
  const setRole = useMutation({ mutationFn: (v: { id: string; role: 'admin' | 'member' }) => client.users.setRole(v), onSuccess: refresh });
  const resetPasskeys = useMutation({ mutationFn: (id: string) => client.users.resetPasskeys({ id }), onSuccess: refresh });
  const toggle = useMutation({ mutationFn: (u: { id: string; active: boolean }) => (u.active ? client.users.deactivate({ id: u.id }) : client.users.reactivate({ id: u.id })), onSuccess: refresh });
  const error = setRole.error ?? toggle.error ?? resetPasskeys.error;
  return (
    <section className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <h2 className="card-title text-base">{t('admin.users')}</h2>
        {users.isError && <ErrorAlert error={users.error} />}
        {error && <p className="text-error text-sm">{errorText(t, error)}</p>}
        <div className="overflow-x-auto">
          <table className="table-sm table">
            <tbody>
              {users.data?.map((u) => (
                <tr key={u.id}>
                  <td className="font-medium">{u.name}</td>
                  <td className="text-base-content/70">{u.email}</td>
                  <td>
                    <select className="select select-xs" value={u.role} aria-label={t('admin.role')} onChange={(e) => setRole.mutate({ id: u.id, role: e.target.value as 'admin' | 'member' })}>
                      <option value="member">{t('admin.roles.member')}</option>
                      <option value="admin">{t('admin.roles.admin')}</option>
                    </select>
                  </td>
                  <td>{u.active ? <span className="badge badge-success badge-soft badge-sm">{t('admin.active')}</span> : <span className="badge badge-ghost badge-sm">{t('admin.inactive')}</span>}</td>
                  <td className="text-base-content/60 text-xs">
                    {t('admin.pluginVersion')}: {u.pluginVersion ?? t('common.none')}
                  </td>
                  <td className="text-end">
                    {u.id !== me.id && (
                      <>
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs"
                          title={t('admin.resetPasskeysHint')}
                          onClick={() => window.confirm(t('admin.resetPasskeysConfirm', { name: u.name })) && resetPasskeys.mutate(u.id)}
                        >
                          {t('admin.resetPasskeys')}
                        </button>
                        <button type="button" className="btn btn-ghost btn-xs" onClick={() => toggle.mutate({ id: u.id, active: u.active })}>
                          {u.active ? t('admin.deactivate') : t('admin.reactivate')}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function Invites() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const invites = useQuery(orpc.invites.list.queryOptions());
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [locale, setLocale] = useState<'de' | 'en'>('de');
  const [result, setResult] = useState<{ email: string; url: string | null } | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.invites.key() });
  const create = useMutation({
    mutationFn: () => client.invites.create({ email, role, locale }),
    onSuccess: async (res) => {
      setResult({ email: res.invite.email, url: res.inviteUrl });
      setEmail('');
      await refresh();
    }
  });
  const resend = useMutation({ mutationFn: (id: string) => client.invites.resend({ id }), onSuccess: async (res) => (res.inviteUrl && setResult({ email: '', url: res.inviteUrl }), refresh()) });
  const revoke = useMutation({ mutationFn: (id: string) => client.invites.revoke({ id }), onSuccess: refresh });
  return (
    <section className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <h2 className="card-title text-base">{t('admin.invites')}</h2>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <input className="input input-sm w-64" type="email" placeholder={t('auth.email')} aria-label={t('auth.email')} value={email} onChange={(e) => setEmail(e.target.value)} required />
          <select className="select select-sm w-auto" value={role} aria-label={t('admin.role')} onChange={(e) => setRole(e.target.value as 'member' | 'admin')}>
            <option value="member">{t('admin.roles.member')}</option>
            <option value="admin">{t('admin.roles.admin')}</option>
          </select>
          <select className="select select-sm w-auto" value={locale} aria-label={t('admin.inviteLanguage')} onChange={(e) => setLocale(e.target.value as 'de' | 'en')}>
            <option value="de">{t('language.de')}</option>
            <option value="en">{t('language.en')}</option>
          </select>
          <button type="submit" className="btn btn-primary btn-sm" disabled={create.isPending}>
            {t('admin.invite')}
          </button>
        </form>
        {create.isError && <p className="text-error text-sm">{errorText(t, create.error)}</p>}
        {result && (
          <div role="status" className="alert alert-info alert-soft text-sm">
            {result.url ? (
              <div className="space-y-1">
                <p>{t('admin.inviteLinkManual')}</p>
                <code className="break-all">{result.url}</code>
              </div>
            ) : (
              <p>{t('admin.inviteSent', { email: result.email })}</p>
            )}
          </div>
        )}
        {invites.data && !invites.data.length && <p className="text-base-content/60 text-sm">{t('admin.noInvites')}</p>}
        <ul className="space-y-1">
          {invites.data?.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium">{i.email}</span>
              <span className="badge badge-ghost badge-xs">{t(`admin.roles.${i.role}`)}</span>
              <span className="text-base-content/50 text-xs">{i.expired ? t('admin.expired') : formatDate(i.expiresAt)}</span>
              <button type="button" className="btn btn-ghost btn-xs ml-auto" onClick={() => resend.mutate(i.id)}>
                {t('admin.resend')}
              </button>
              <button type="button" className="btn btn-ghost btn-xs" onClick={() => revoke.mutate(i.id)}>
                {t('admin.revokeInvite')}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Health() {
  const { t } = useTranslation();
  const health = useQuery(orpc.system.health.queryOptions());
  return (
    <section className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <h2 className="card-title text-base">{t('home.status')}</h2>
        {health.isError && <p className="text-error text-sm">{t('home.statusUnreachable')}</p>}
        {health.data && (
          <>
            <p data-testid="health-status" className={health.data.status === 'ok' ? 'text-success' : 'text-warning'}>
              {health.data.status === 'ok' ? t('home.statusOk') : t('home.statusDegraded', { failed: t('checks.database') })}
            </p>
            <p className="text-base-content/60 text-sm">{t('home.version', { version: health.data.version })}</p>
          </>
        )}
      </div>
    </section>
  );
}
